import { getEnv } from 'common-nextjs';
import { useField, useFormikContext } from 'formik';
import React, { useEffect, useRef } from 'react';
import { Field } from 'ui';
import { AddressFormValues } from '~/components/Address/AddressForm';
import useScript from '~/hooks/useScript';

type Props = {
  label: string;
  name: string;
};

const FormikAddressAutocompleteField: React.FC<Props> = ({ label, name }) => {
  const formik = useFormikContext<AddressFormValues>();
  const [field, meta, helpers] = useField(name);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocomplete = useRef<google.maps.places.Autocomplete | null>(null);

  const [loaded, error] = useScript(
    `https://maps.googleapis.com/maps/api/js?key=${getEnv(
      'GOOGLE_CLOUD_MAPS_AUTOCOMPLETE_API_KEY',
    )}&libraries=places`,
  );

  function handleAddressSelected() {
    // Get the place details from the autocomplete object.
    const place = autocomplete.current?.getPlace();

    if (!place?.address_components) {
      return;
    }

    const newAddress: Partial<AddressFormValues> = {
      street_1: '',
      street_2: '',
    };

    for (let i = 0; i < place.address_components.length; i++) {
      const addressComponent = place.address_components[i];
      const addressType = addressComponent.types[0];

      switch (addressType) {
        case 'street_number':
          // Add a space
          newAddress.street_1 = `${addressComponent.short_name} `;
          formik.setFieldValue('street_1', newAddress.street_1);
          break;

        case 'route':
          newAddress.street_1 += addressComponent.short_name;
          formik.setFieldValue('street_1', newAddress.street_1);
          break;

        case 'locality':
          newAddress.city = addressComponent.long_name;
          formik.setFieldValue('city', newAddress.city);
          break;

        case 'administrative_area_level_1':
          newAddress.state = addressComponent.short_name;
          formik.setFieldValue('state', newAddress.state);
          break;

        case 'country':
          newAddress.country = addressComponent.short_name;
          formik.setFieldValue('country', newAddress.country);
          break;

        case 'postal_code':
          newAddress.zip = addressComponent.short_name;
          formik.setFieldValue('zip', newAddress.zip);
          break;
      }
    }
  }

  useEffect(() => {
    if (loaded && !error && 'google' in window && inputRef.current) {
      autocomplete.current = new google.maps.places.Autocomplete(
        inputRef.current,
        { types: ['geocode'] },
      );

      autocomplete.current.setFields(['address_component']);
      autocomplete.current.addListener('place_changed', handleAddressSelected);

      return () => {
        /* if (autocomplete.current?.removeListener) {
          autocomplete.current?.removeListener(
            'place_changed',
            handleAddressSelected,
          );
        } */

        document.querySelector('.pac-container')?.remove();
      };
    }
  }, [loaded, error, inputRef]);

  return (
    <Field
      {...field}
      error={meta.error}
      name={name}
      label={label}
      ref={inputRef}
    />
  );
};

export default FormikAddressAutocompleteField;
